import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    Chip,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Grid,
    Divider
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import {
    getCategoryOptions,
    createCms,
    getCms,
    deleteCms,
    updateCms
} from '../Apis/CategoriesApis';

// Helper to strip HTML tags for table preview
const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim();
};

const CategoryOption = () => {
    const { categoryId } = useParams();
    const navigate = useNavigate();

    // Options State
    const [options, setOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [isOptionsLoading, setIsOptionsLoading] = useState(false);

    // CMS State
    const [cmsContent, setCmsContent] = useState([]);
    const [isCmsLoading, setIsCmsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [cmsDialogOpen, setCmsDialogOpen] = useState(false);
    const [newCms, setNewCms] = useState({ plan: '', description: '', PriceText: '' });
    const [editingCmsId, setEditingCmsId] = useState(null);

    // Menu State for CMS actions
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedCmsRow, setSelectedCmsRow] = useState(null);

    const fetchOptions = async () => {
        setIsOptionsLoading(true);
        try {
            const response = await getCategoryOptions(categoryId);
            if (response.data && response.data.status) {
                const data = response.data.data || [];
                setOptions(data);
                if (data.length > 0 && !selectedOption) {
                    setSelectedOption(data[0]);
                }
            }
        } catch (error) {
            console.error("Error fetching options:", error);
        } finally {
            setIsOptionsLoading(false);
        }
    };

    const fetchCmsContent = async () => {
        if (!selectedOption && !categoryId) return;
        setIsCmsLoading(true);
        try {
            const response = await getCms(categoryId, selectedOption?.id);
            if (response.data && response.data.status) {
                setCmsContent(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching CMS content:", error);
        } finally {
            setIsCmsLoading(false);
        }
    };

    useEffect(() => {
        if (categoryId) {
            fetchOptions();
        }
    }, [categoryId]);

    useEffect(() => {
        fetchCmsContent();
    }, [categoryId, selectedOption]);

    const handleCmsDialogOpen = () => {
        setEditingCmsId(null);
        setNewCms({ plan: '', description: '', PriceText: '' });
        setCmsDialogOpen(true);
    };

    const handleCmsDialogClose = () => {
        setCmsDialogOpen(false);
    };

    const onEditorChange = (content) => {
        setNewCms(prev => ({ ...prev, description: content }));
    };

    const handleCmsEdit = (cms) => {
        setEditingCmsId(cms.id);
        setNewCms({
            plan: cms.plan || '',
            description: cms.description || '',
            PriceText: cms.PriceText || ''
        });

        setAnchorEl(null);
        setCmsDialogOpen(true);
    };

    const handleCmsDelete = async (cms) => {
        if (window.confirm("Are you sure you want to delete this CMS content?")) {
            try {
                const response = await deleteCms(cms.id);
                if (response.data && response.data.status) {
                    fetchCmsContent();
                }
            } catch (error) {
                console.error("Error deleting CMS:", error);
            }
        }
        setAnchorEl(null);
    };

    const handleCmsSubmit = async () => {
        if (!selectedOption) {
            alert("Please select an option first");
            return;
        }
        setIsSubmitting(true);
        try {
            const htmlContent = newCms.description;
            const formData = new FormData();
            formData.append('description', htmlContent);
            formData.append('plan', newCms.plan);
            formData.append('PriceText', newCms.PriceText || '');
            formData.append('categoryId', categoryId);
            formData.append('optionId', selectedOption.id);
            // Title is removed from form but added as empty if mandatory
            formData.append('title', '');

            let response;
            if (editingCmsId) {
                response = await updateCms(editingCmsId, formData);
            } else {
                response = await createCms(formData);
            }

            if (response.data && response.data.status) {
                setCmsDialogOpen(false);
                fetchCmsContent();
            } else {
                alert(response.data.message || "Failed to save CMS content");
            }
        } catch (error) {
            console.error("Error saving CMS:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleMenuOpen = (event, cms) => {
        setAnchorEl(event.currentTarget);
        setSelectedCmsRow(cms);
    };

    return (
        <Container maxWidth="xl" >
            <Grid container spacing={3}>
                <Grid item size={{ xs: 12, md: 3 }}>
                    <Paper sx={{ p: 2, height: '100%', minHeight: '70vh' }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            Category Options
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {isOptionsLoading ? (
                            <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
                        ) : (
                            <Box display="flex" flexDirection="column" gap={1}>
                                {options.length === 0 ? (
                                    <Typography variant="body2" color="textSecondary">No options found.</Typography>
                                ) : (
                                    options.map((option) => (
                                        <Button disableFocusRipple disableElevation disableTouchRipple disableRipple
                                            key={option.id} variant={selectedOption?.id === option.id ? "contained" : "outlined"}
                                            onClick={() => setSelectedOption(option)} fullWidth
                                            sx={{ justifyContent: 'space-between', textTransform: 'none' }}
                                            endIcon={<ArrowForwardIosIcon sx={{ fontSize: 12 }} />}
                                        >
                                            {option.optionName || option.name}
                                        </Button>
                                    ))
                                )}
                            </Box>
                        )}
                    </Paper>
                </Grid>

                <Grid item size={{ xs: 12, md: 9 }}>
                    <Paper sx={{ p: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h5">
                                CMS Content {selectedOption ? `for ${selectedOption.optionName || selectedOption.name}` : ''}
                            </Typography>
                            <Box display="flex" gap={2}>
                                <Button disableFocusRipple disableElevation disableTouchRipple disableRipple
                                    variant="contained"
                                    color="primary"
                                    onClick={handleCmsDialogOpen}
                                    disabled={!selectedOption}
                                >
                                    Add CMS
                                </Button>
                            </Box>
                        </Box>

                        {isCmsLoading ? (
                            <Box display="flex" justifyContent="center" minHeight="40vh" alignItems="center">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Description</TableCell>
                                            <TableCell>Plan</TableCell>
                                            <TableCell>Price Text</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cmsContent.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center">No CMS content found for this option.</TableCell>
                                            </TableRow>
                                        ) : (
                                            cmsContent.map((cms, index) => (
                                                <TableRow key={cms.id}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>
                                                        <Box
                                                            sx={{
                                                                maxWidth: '250px',
                                                                maxHeight: '150px', // Allow more height for full content
                                                                overflowY: 'auto',
                                                                padding: '4px',
                                                                '& *': {
                                                                    fontSize: '13px !important',
                                                                    margin: '0 !important',
                                                                    lineHeight: '1.4 !important',
                                                                },
                                                                '& ul, & ol': {
                                                                    paddingLeft: '20px !important'
                                                                }
                                                            }}
                                                            dangerouslySetInnerHTML={{ __html: cms.description }}
                                                        />
                                                    </TableCell>
                                                    <TableCell><Chip label={cms.plan || 'N/A'} size="small" /></TableCell>
                                                    <TableCell><Typography variant="body2">{cms.PriceText || 'N/A'}</Typography></TableCell>
                                                    <TableCell align="right">
                                                        <IconButton size="small" disableTouchRipple onClick={(e) => handleMenuOpen(e, cms)}>
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* CMS Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem onClick={() => handleCmsEdit(selectedCmsRow)}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Edit</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleCmsDelete(selectedCmsRow)}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>

            {/* CMS Dialog */}
            <Dialog open={cmsDialogOpen} onClose={handleCmsDialogClose} maxWidth="md" fullWidth>
                <DialogTitle>{editingCmsId ? 'Edit CMS Content' : 'Add CMS Content'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Plan Type"
                        fullWidth
                        variant="outlined"
                        placeholder="e.g. Basic, Premium..."
                        value={newCms.plan}
                        onChange={(e) => setNewCms({ ...newCms, plan: e.target.value })}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TextField
                        margin="dense"
                        label="Price Text (e.g. ['Best Price', 'Limited Offer'])"
                        fullWidth
                        variant="outlined"
                        value={newCms.PriceText}
                        onChange={(e) => setNewCms({ ...newCms, PriceText: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <Typography variant="body2" sx={{ mb: 1 }}>Description</Typography>
                    <Box sx={{
                        '& .ql-container': {
                            minHeight: '250px',
                            fontSize: '14px'
                        },
                        '& .ql-editor': {
                            minHeight: '250px'
                        }
                    }}>
                        <ReactQuill
                            theme="snow"
                            value={newCms.description}
                            onChange={onEditorChange}
                            modules={{
                                toolbar: [
                                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                                    ['bold', 'italic', 'underline', 'strike'],
                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                    [{ 'align': [] }],
                                    ['link', 'clean']
                                ],
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button disableRipple disableElevation disableFocusRipple disableFocus onClick={handleCmsDialogClose}>Cancel</Button>
                    <Button disableRipple disableElevation disableFocusRipple disableFocus
                        onClick={handleCmsSubmit}
                        variant="contained"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default CategoryOption;
